import { minBy, uniqBy } from 'lodash-es';
import {
  collectionStatusApiConstMap,
  TCollectionStatus,
} from 'src/api/consts/CollectionStatus';

const hasTickets = (collection: $TSFixMe) => {
  return (collection?.pickup_order?.created_tickets ?? []).length !== 0;
};

const getStatus = (collection: $TSFixMe): TCollectionStatus => {
  const collHasTickets = hasTickets(collection);
  const collectionStatus = collectionStatusApiConstMap[collection.status];

  if (collHasTickets && collectionStatus === 'COMPLETE') {
    return 'COMPLETE_WITH_TICKET';
  }

  return collectionStatus;
};

// Which status one entry shows for several containers of a waste fraction on
// the same day: a problem first, then a collection still to come (pending
// before scheduled, so a half-planned day does not read as planned), and
// emptied only once every container is, a driver's report ahead of a clean
// emptying.
const MERGED_STATUS_ORDER: TCollectionStatus[] = [
  'COMPLETE_NOT_EMPTIED',
  'MISSED',
  'PENDING',
  'SCHEDULED',
  'COMPLETE_WITH_TICKET',
  'COMPLETE',
];

const mergedStatusRank = (collection: $TSFixMe) => {
  const rank = MERGED_STATUS_ORDER.indexOf(getStatus(collection));
  return rank === -1 ? MERGED_STATUS_ORDER.length : rank;
};

// The fraction as the citizen sees it: by name, so two fractions that share
// a name (and read the same in a calendar cell) count as one.
const wasteFractionKey = (collection: $TSFixMe): string =>
  collection?.container?.waste_fraction?.name?.trim() ?? '';

/**
 * One collection-log row per waste fraction, in the order the fractions first
 * appear. The log has a row per agreement, so a property with nine food-waste
 * bins would otherwise list "Matavfall" nine times on every collection day.
 * A merged row carries the group's status per MERGED_STATUS_ORDER and every
 * ticket of the group, so clicking it still opens all the driver's reports.
 */
const mergeByWasteFraction = (collections: $TSFixMe[]): $TSFixMe[] => {
  const groups = new Map<string, $TSFixMe[]>();
  collections.forEach((collection) => {
    const key = wasteFractionKey(collection);
    groups.set(key, [...(groups.get(key) ?? []), collection]);
  });

  return [...groups.values()].map((group) => {
    if (group.length === 1) return group[0];
    // A bin can have two rows on one day (its own round plus an extra-route
    // stop, or a household and a næring round): it is emptied if either stop
    // emptied it, so keep each bin's best row before picking the group's most
    // pressing one. Rows without a container id stay separate.
    const bestPerBin = new Map<string, $TSFixMe>();
    group.forEach((collection, index) => {
      const binKey = collection?.container?.id ?? `row-${index}`;
      const best = bestPerBin.get(binKey);
      if (!best || mergedStatusRank(collection) > mergedStatusRank(best)) {
        bestPerBin.set(binKey, collection);
      }
    });
    const shown = minBy([...bestPerBin.values()], mergedStatusRank);
    // A bin with more than one agreement covering the day has a row per
    // agreement, all on the bin's one pickup order and so its tickets.
    const tickets = uniqBy(
      group.flatMap(
        (collection) => collection?.pickup_order?.created_tickets ?? []
      ),
      (ticket: $TSFixMe) => ticket?.id ?? ticket
    );
    return tickets.length
      ? {
          ...shown,
          pickup_order: { ...shown.pickup_order, created_tickets: tickets },
        }
      : shown;
  });
};

export const apiHelperCollections = {
  hasTickets,
  getStatus,
  wasteFractionKey,
  mergeByWasteFraction,
};
